// src/Components/Pages/ProfilePage.tsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { api } from "../../api";
import Sidebar from "../Partials/Sidebar";
import TrialBanner from "../Partials/TrialBanner";
import NapkinSubscriptionModal from "../../Components/Billing/NapkinSubscriptionModal";

import "../../Styles/DashboardPageStyles/Dashboard.css";
import "../../Styles/Pages/Profile.css";

import { useUser } from "../../Context/UserContext";
import { deleteAccount } from "../../api/account";

function ProfilePage() {
  const navigate = useNavigate();
  const {
    user,
    loading,
    fetchUserProfile,
    logout,
    trialStatus,
    fetchTrialStatus,
  } = useUser();

  // Local profile state
  const [profile, setProfile] = useState<{
    name: string;
    email: string;
    loginMethod: string;
  } | null>(null);

  // Name editing — always editable, saves on blur / Enter
  const [nameInput, setNameInput] = useState("");
  const [nameLoaded, setNameLoaded] = useState(false);
  const [nameSaving, setNameSaving] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);

  // Prevent multiple profile fetches
  const fetchedOnce = useRef(false);

  // Fetch profile ONCE after auth is resolved and user exists
  useEffect(() => {
    if (!loading && user && !fetchedOnce.current) {
      fetchedOnce.current = true;
      let cancelled = false;
      (async () => {
        const p = await fetchUserProfile();
        if (cancelled) return;
        setProfile(p ?? null);
        if (p?.name && !nameLoaded) {
          setNameInput(p.name);
          setNameLoaded(true);
        }
      })();
      return () => {
        cancelled = true;
        fetchedOnce.current = false;
      };
    }
  }, [user, loading, fetchUserProfile, nameLoaded]);

  // If RequireAuth is in your router, this is just a safety net
  useEffect(() => {
    if (!loading && !user) {
      navigate("/login", { replace: true });
    }
  }, [loading, user, navigate]);

  // ====== Billing modal state ======
  const [subOpen, setSubOpen] = useState(false);

  // ====== Delete account state ======
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  const deleteInputRef = useRef<HTMLInputElement>(null);

  // Close delete modal on Escape
  useEffect(() => {
    if (!deleteModalOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setDeleteModalOpen(false);
        setDeleteConfirmInput("");
        setDeletePassword("");
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [deleteModalOpen]);

  // Auto-focus delete confirmation input
  useEffect(() => {
    if (deleteModalOpen && deleteInputRef.current) {
      deleteInputRef.current.focus();
    }
  }, [deleteModalOpen]);

  const cancelSub = async () => {
    try {
      await api.post("/api/billing/cancel");
      setSubOpen(false);
      fetchTrialStatus();
    } catch (e) {
      console.error(e);
      toast.error("Could not cancel subscription. Please try again.");
    }
  };

  // ====== Handlers ======
  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === profile?.name) return;
    setNameSaving(true);
    try {
      const res = await api.patch("/api/update-profile", { name: trimmed });
      if (res.data.success) {
        setProfile((prev) => (prev ? { ...prev, name: res.data.name } : prev));
        setNameInput(res.data.name);
        setNameSaved(true);
        setTimeout(() => setNameSaved(false), 2000);
      }
    } catch {
      toast.error("Could not update name. Please try again.");
    } finally {
      setNameSaving(false);
    }
  };

  const handleResetPassword = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    try {
      const email = profile?.email?.trim();
      if (!email) {
        toast.error("Could not send reset email. Please try again.");
        return;
      }
      await api.post("/api/forgot-password", { email });
      toast.success("A reset link was sent to your email.");
    } catch (err: any) {
      const msg = err?.response?.data?.message || "An error occurred while sending the reset request.";
      toast.error(msg);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const handleDeleteAccount = async () => {
    const isGoogle = profile?.loginMethod === "google";

    // Validate confirmation
    if (isGoogle && deleteConfirmInput !== "DELETE") return;
    if (!isGoogle && !deletePassword) return;

    try {
      setDeleting(true);
      await deleteAccount(isGoogle ? undefined : deletePassword);
      toast.success("Account deleted");
      // Clear local state and redirect
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch {}
      await logout();
      navigate("/login", { replace: true });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || "Could not delete account. Please try again.";
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  // ====== Tabs ======
  const [activeTab, setActiveTab] = useState<
    "Profile" | "Settings" | "Billing"
  >("Profile");

  // Derived billing helpers
  const hasSub = trialStatus?.hasSubscription ?? false;
  const isTrialing = trialStatus?.isTrialing ?? false;
  const trialExpired = trialStatus?.trialExpired ?? false;
  const daysLeft = trialStatus?.daysRemaining ?? 0;

  const tierName =
    trialStatus?.plan === "business"
      ? "Business"
      : trialStatus?.plan === "enterprise"
      ? "Enterprise"
      : trialStatus?.plan === "pro"
      ? "Pro"
      : "Free";

  const planDisplay = hasSub
    ? tierName
    : isTrialing
    ? "Free Trial"
    : trialExpired
    ? "Trial Ended"
    : "Free";

  const planBadgeStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    marginLeft: 8,
    background: hasSub ? "#ecfdf3" : trialExpired ? "#fef3c7" : "#eff6ff",
    color: hasSub ? "#065f46" : trialExpired ? "#92400e" : "#1e40af",
  };

  // Style helper for form rows
  const fieldRowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "10px 0",
    borderBottom: "1px solid #f1f5f9",
  };

  const fieldLabelStyle: React.CSSProperties = {
    fontSize: 12.5,
    fontWeight: 600,
    color: "#374151",
    minWidth: 90,
  };

  const fieldValueStyle: React.CSSProperties = {
    fontSize: 12.5,
    color: "#111827",
    flex: 1,
  };

  const renderContent = () => {
    switch (activeTab) {
      case "Profile":
        return (
          <>
            {/* PERSONAL INFO */}
            <div className="card settings-card">
              <h3>Personal Information</h3>

              {/* Name row */}
              <div style={fieldRowStyle}>
                <span style={fieldLabelStyle}>Name</span>
                <div style={{ flex: 1, display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="text"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveName();
                      if (e.key === "Escape") setNameInput(profile?.name ?? "");
                    }}
                    onBlur={handleSaveName}
                    placeholder="Your name"
                    disabled={nameSaving}
                    style={{
                      flex: 1,
                      height: 32,
                      padding: "0 10px",
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      fontSize: 12.5,
                      fontFamily: "inherit",
                      outline: "none",
                      background: "#fff",
                      transition: "border-color 0.14s ease",
                    }}
                  />
                  {nameSaving && (
                    <span style={{ fontSize: 11, color: "#9ca3af", whiteSpace: "nowrap" }}>
                      Saving...
                    </span>
                  )}
                  {nameSaved && (
                    <span style={{ color: '#22c55e', marginLeft: 8, fontSize: 13, transition: 'opacity 0.3s' }}>
                      &#10003; Saved
                    </span>
                  )}
                </div>
              </div>

              {/* Email row */}
              <div style={fieldRowStyle}>
                <span style={fieldLabelStyle}>Email</span>
                <span style={fieldValueStyle}>{profile?.email ?? "--"}</span>
              </div>

              {/* Login method row */}
              <div style={{ ...fieldRowStyle, borderBottom: "none" }}>
                <span style={fieldLabelStyle}>Login method</span>
                <span style={fieldValueStyle}>
                  {profile?.loginMethod === "google"
                    ? "Google"
                    : "Email & password"}
                </span>
              </div>
            </div>

            {/* UPGRADE CARD (shown when not subscribed) */}
            {!hasSub && (
              <div className="card settings-card" style={{ background: "#eff6ff", border: "1px solid #bfdbfe" }}>
                <h3 style={{ color: "#1e40af" }}>
                  {isTrialing ? "Upgrade your plan" : trialExpired ? "Subscribe to continue" : "Get started"}
                </h3>
                <p style={{ fontSize: 12.5, color: "#1e40af", margin: "0 0 10px", opacity: 0.8 }}>
                  {isTrialing
                    ? `You have ${daysLeft} day${daysLeft === 1 ? "" : "s"} left in your trial. Subscribe to keep all features.`
                    : trialExpired
                    ? "Your trial has ended. Subscribe to continue sending documents."
                    : "Subscribe to unlock all features."}
                </p>
                <button
                  className="primary-button"
                  type="button"
                  onClick={() => setSubOpen(true)}
                >
                  {isTrialing ? "Subscribe now" : trialExpired ? "Subscribe to continue" : "Subscribe"}
                </button>
              </div>
            )}

            {/* SECURITY */}
            {profile?.loginMethod !== "google" && (
              <div className="card settings-card">
                <h3>Security</h3>
                <p
                  style={{
                    fontSize: 12.5,
                    color: "#6b7280",
                    margin: "0 0 10px",
                  }}
                >
                  Need to change your password? We'll send a reset link to your
                  email.
                </p>
                <button
                  onClick={handleResetPassword}
                  className="secondary-button"
                  type="button"
                >
                  Send password reset email
                </button>
              </div>
            )}
          </>
        );

      case "Settings":
        return (
          <>
            <div className="card settings-card">
              <h3>Notifications</h3>
              <p
                style={{
                  fontSize: 12.5,
                  color: "#6b7280",
                  margin: "0 0 6px",
                }}
              >
                Email notifications are sent for document activity like
                signatures, reminders, and completions.
              </p>
              <p
                style={{
                  fontSize: 11.5,
                  color: "#9ca3af",
                  margin: 0,
                }}
              >
                More notification settings coming soon.
              </p>
            </div>

            <div className="card settings-card">
              <h3>Account</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button
                  onClick={handleLogout}
                  className="secondary-button"
                  type="button"
                >
                  Log out of this device
                </button>
              </div>
            </div>

            {/* DANGER ZONE — Delete Account */}
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fca5a5",
                borderRadius: 12,
                padding: "20px 24px",
                marginTop: 8,
              }}
            >
              <h3 style={{ color: "#991b1b", margin: "0 0 6px", fontSize: 14 }}>
                Danger Zone
              </h3>
              <p
                style={{
                  fontSize: 12.5,
                  color: "#991b1b",
                  margin: "0 0 14px",
                  lineHeight: 1.5,
                }}
              >
                This action is permanent and cannot be undone. All your documents,
                templates, and account data will be deleted.
              </p>

              <button
                type="button"
                onClick={() => setDeleteModalOpen(true)}
                style={{
                  background: "#dc2626",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 18px",
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Delete Account
              </button>
            </div>
          </>
        );

      case "Billing":
        return (
          <>
            {/* PLAN CARD */}
            <div className="card plan-card">
              <h3>
                Current Plan
                <span style={planBadgeStyle}>{planDisplay}</span>
              </h3>

              <div className="plan-details">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  {hasSub && (
                    <p style={{ margin: 0 }}>
                      You're on the <strong>{tierName}</strong> plan. Thank you for
                      subscribing!
                    </p>
                  )}
                  {isTrialing && (
                    <p style={{ margin: 0 }}>
                      You have{" "}
                      <strong>
                        {daysLeft} day{daysLeft === 1 ? "" : "s"}
                      </strong>{" "}
                      remaining in your free trial.
                      {trialStatus?.trialEndsAt && (
                        <span style={{ color: "#6b7280" }}>
                          {" "}
                          Ends{" "}
                          {new Date(
                            trialStatus.trialEndsAt
                          ).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                          .
                        </span>
                      )}
                    </p>
                  )}
                  {trialExpired && (
                    <p style={{ margin: 0 }}>
                      Your free trial has ended. Subscribe to keep using all
                      features.
                    </p>
                  )}
                  {!hasSub && !isTrialing && !trialExpired && (
                    <p style={{ margin: 0 }}>You're on the free plan.</p>
                  )}
                </div>
              </div>

              {!hasSub && (
                <button
                  className="primary-button"
                  type="button"
                  onClick={() => setSubOpen(true)}
                >
                  {isTrialing
                    ? "Subscribe now"
                    : trialExpired
                    ? "Subscribe to continue"
                    : "Subscribe"}
                </button>
              )}
              {hasSub && (
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => setSubOpen(true)}
                >
                  Manage subscription
                </button>
              )}
            </div>

            {/* PAYMENT METHOD */}
            <div className="card billing-card">
              <h3>Payment Method</h3>
              <div className="billing-info">
                {hasSub ? (
                  <p style={{ margin: 0 }}>
                    Your payment method is managed through Stripe.
                  </p>
                ) : (
                  <p style={{ margin: 0 }}>
                    No payment method on file. Subscribe to add one.
                  </p>
                )}
              </div>
            </div>

            {/* TRIAL TIMELINE */}
            {(isTrialing || trialExpired) && (
              <div className="card usage-card">
                <h3>Trial Timeline</h3>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    fontSize: 12.5,
                    color: "#4b5563",
                  }}
                >
                  {trialStatus?.trialEndsAt && (
                    <>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>Trial started</span>
                        <span style={{ fontWeight: 600, color: "#111827" }}>
                          {new Date(
                            new Date(trialStatus.trialEndsAt).getTime() -
                              14 * 24 * 60 * 60 * 1000
                          ).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>
                          {trialExpired ? "Trial ended" : "Trial ends"}
                        </span>
                        <span
                          style={{
                            fontWeight: 600,
                            color: trialExpired ? "#92400e" : "#111827",
                          }}
                        >
                          {new Date(
                            trialStatus.trialEndsAt
                          ).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </span>
                      </div>
                    </>
                  )}
                  {isTrialing && (
                    <div
                      style={{
                        marginTop: 4,
                        height: 6,
                        borderRadius: 3,
                        background: "#e5e7eb",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          borderRadius: 3,
                          background: "#0f172a",
                          width: `${Math.max(
                            4,
                            ((14 - daysLeft) / 14) * 100
                          )}%`,
                          transition: "width 0.3s ease",
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        );

      default:
        return null;
    }
  };

  // ====== Render ======
  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="dashboard-loading-card">
          <div className="dashboard-loading-dot" />
          <div className="dashboard-loading-dot" />
          <div className="dashboard-loading-dot" />
        </div>
      </div>
    );
  }

  if (!user) return null;

  const initials =
    profile?.name
      ?.split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0]?.toUpperCase())
      .join("") || "U";

  return (
    <div className="dashboard">
      <Sidebar active="Profile" />

      <div className="dashboard-main">
        <TrialBanner />
        <header className="dashboard-header">
          <div className="dashboard-header-left">
            <h1 className="dashboard-title">Account &amp; Profile</h1>
            <p className="dashboard-subtitle">
              Manage your details, password, and subscription.
            </p>
          </div>
        </header>

        <div className="profile-header">
          {/* USER INFO */}
          <div className="profile-user-info">
            <div className="profile-avatar-circle">{initials}</div>
            <div className="profile-user-main">
              <h2 className="profile-user-name">
                {profile?.name ?? "Your account"}
              </h2>
              <p className="profile-user-email">{profile?.email ?? ""}</p>
              {trialStatus && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "3px 10px",
                    borderRadius: 999,
                    fontSize: 10.5,
                    fontWeight: 700,
                    marginTop: 4,
                    background: hasSub
                      ? "#ecfdf3"
                      : trialExpired
                      ? "#fef3c7"
                      : "#eff6ff",
                    color: hasSub
                      ? "#065f46"
                      : trialExpired
                      ? "#92400e"
                      : "#1e40af",
                  }}
                >
                  {planDisplay}
                  {isTrialing && ` · ${daysLeft}d left`}
                </span>
              )}
            </div>
          </div>

          {/* TABS */}
          <div className="profile-tabs">
            {(["Profile", "Settings", "Billing"] as const).map((tab) => (
              <div
                key={tab}
                className={`tab ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </div>
            ))}
          </div>

          {/* TAB CONTENT */}
          <div className="profile-content">{renderContent()}</div>
        </div>
      </div>

      {/* DELETE ACCOUNT MODAL */}
      {deleteModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 999999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.4)",
          }}
          onClick={() => {
            setDeleteModalOpen(false);
            setDeleteConfirmInput("");
            setDeletePassword("");
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              maxWidth: 420,
              width: "90%",
              padding: 24,
              boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
              animation: "fadeIn 0.2s ease-out",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ color: "#dc2626", margin: "0 0 6px", fontSize: 16, fontWeight: 700 }}>
              Delete Account
            </h3>
            <p style={{ fontSize: 12.5, color: "#6b7280", margin: "0 0 18px", lineHeight: 1.5 }}>
              This action is permanent and cannot be undone. All your documents,
              templates, and account data will be deleted.
            </p>

            {profile?.loginMethod === "google" ? (
              <>
                <label style={{ fontSize: 12.5, fontWeight: 600, color: "#991b1b", display: "block", marginBottom: 6 }}>
                  Type DELETE to confirm
                </label>
                <input
                  ref={deleteInputRef}
                  type="text"
                  value={deleteConfirmInput}
                  onChange={(e) => setDeleteConfirmInput(e.target.value)}
                  placeholder="DELETE"
                  style={{
                    width: "100%",
                    height: 36,
                    padding: "0 12px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    fontSize: 13,
                    fontFamily: "inherit",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </>
            ) : (
              <>
                <label style={{ fontSize: 12.5, fontWeight: 600, color: "#991b1b", display: "block", marginBottom: 6 }}>
                  Enter your password to confirm
                </label>
                <input
                  ref={deleteInputRef}
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Your password"
                  style={{
                    width: "100%",
                    height: 36,
                    padding: "0 12px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    fontSize: 13,
                    fontFamily: "inherit",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
              <button
                type="button"
                onClick={() => {
                  setDeleteModalOpen(false);
                  setDeleteConfirmInput("");
                  setDeletePassword("");
                }}
                className="secondary-button"
                style={{ fontSize: 12.5, height: 34, padding: "0 16px" }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  deleting ||
                  (profile?.loginMethod === "google"
                    ? deleteConfirmInput !== "DELETE"
                    : !deletePassword)
                }
                onClick={handleDeleteAccount}
                style={{
                  background:
                    deleting ||
                    (profile?.loginMethod === "google"
                      ? deleteConfirmInput !== "DELETE"
                      : !deletePassword)
                      ? "#f87171"
                      : "#dc2626",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "0 18px",
                  height: 34,
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor:
                    deleting ||
                    (profile?.loginMethod === "google"
                      ? deleteConfirmInput !== "DELETE"
                      : !deletePassword)
                      ? "not-allowed"
                      : "pointer",
                  fontFamily: "inherit",
                  opacity:
                    deleting ||
                    (profile?.loginMethod === "google"
                      ? deleteConfirmInput !== "DELETE"
                      : !deletePassword)
                      ? 0.6
                      : 1,
                }}
              >
                {deleting ? "Deleting..." : "Delete Account"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SUBSCRIPTION MODAL */}
      <NapkinSubscriptionModal
        isOpen={subOpen}
        currentTier={(trialStatus?.plan || "free") as any}
        currentFrequency={(trialStatus as any)?.planFrequency || null}
        hasSubscription={hasSub}
        renewalDate={trialStatus?.trialEndsAt || undefined}
        onClose={() => setSubOpen(false)}
        onCancel={cancelSub}
        onSuccess={() => {
          fetchTrialStatus();
          setSubOpen(false);
        }}
        onError={(e) => console.error(e)}
      />
    </div>
  );
}

export default ProfilePage;
